import type { AxiosInstance } from "axios";
import type {
  EligibilityInputs,
  EligibilityOutcome,
  EligibilityPolicyBody,
  EligibilityCriterionResult,
} from "./eligibilityTypes";
export interface EligibilityCourse {
  offeringId: number;
  universityId: number;
  institutionName: string;
  programmeName: string;
  canApply: boolean;
  availability: string;
  startDate?: string | null;
  intakeDates?: string | null;
}
export interface EligibilityPolicy extends EligibilityPolicyBody {
  id: number;
  status: "DRAFT" | "APPROVED";
  lifecycle?: "DRAFT" | "CURRENT" | "SUPERSEDED" | "SCHEDULED" | "EXPIRED";
  currentVersionId?: number | null;
  offeringId: number;
  createdAt: string;
  approvedAt?: string | null;
  createdBy: number;
  approvedBy?: number | null;
}
export interface EligibilityResult {
  outcome: EligibilityOutcome;
  criteria: EligibilityCriterionResult[];
  disclaimer: string;
  reason?: string;
}
export interface EligibilityReview {
  id: number;
  checkId: number;
  status: string;
  ownerAdminId: number | null;
  message: string;
  decisionOutcome?: EligibilityOutcome | null;
  decisionNote?: string | null;
  decisionValidUntil?: string | null;
  decidedAt?: string | null;
  createdAt: string;
  updatedAt: string;
  events: {
    at: string;
    type: string;
    note?: string;
    outcome?: EligibilityOutcome;
    actorId?: number;
  }[];
}
export interface EligibilityCheck {
  id: number;
  offeringId: number;
  studentProfileId: number | null;
  criteriaId: number | null;
  intake: string;
  inputs: EligibilityInputs;
  policySnapshot: Partial<EligibilityPolicyBody> | null;
  result: EligibilityResult;
  courseSnapshot: EligibilityCourse;
  createdAt: string;
  review?: EligibilityReview | null;
}
export interface EligibilityContext {
  course: EligibilityCourse;
  criteria: EligibilityPolicy[];
  students: { id: number; firstName: string; lastName: string }[];
  studentTotal?: number;
  intakes: string[];
  intakeSource: "offering" | "programme" | null;
  intakeSourceText: string | null;
}
export const eligibilityApi = (api: AxiosInstance) => ({
  context: async (
    offeringId: number,
    signal?: AbortSignal,
  ): Promise<EligibilityContext> =>
    (await api.get("/eligibility/context", { params: { offeringId }, signal }))
      .data.data,
  intakes: async (
    offeringId: number,
    signal?: AbortSignal,
  ): Promise<{
    intakes: string[];
    source: "offering" | "programme" | null;
    sourceText: string | null;
  }> =>
    (
      await api.get(`/eligibility/admin/offerings/${offeringId}/intakes`, {
        signal,
      })
    ).data.data,
  students: async (
    search = "",
    page = 1,
    signal?: AbortSignal,
  ): Promise<{
    items: { id: number; firstName: string; lastName: string }[];
    total: number;
    page: number;
  }> =>
    (
      await api.get("/eligibility/students", {
        params: { search, page },
        signal,
      })
    ).data.data,
  prefill: async (
    id: number,
    signal?: AbortSignal,
  ): Promise<{ inputs: EligibilityInputs }> =>
    (await api.get(`/eligibility/students/${id}/prefill`, { signal })).data
      .data,
  createCheck: async (body: {
    offeringId: number;
    studentProfileId?: number;
    intake: string;
    inputs: EligibilityInputs;
  }): Promise<EligibilityCheck> =>
    (await api.post("/eligibility/checks", body)).data.data,
  checks: async (
    page = 1,
    signal?: AbortSignal,
  ): Promise<{ items: EligibilityCheck[]; total: number; page: number }> =>
    (await api.get("/eligibility/checks", { params: { page }, signal })).data
      .data,
  check: async (id: number, signal?: AbortSignal): Promise<EligibilityCheck> =>
    (await api.get(`/eligibility/checks/${id}`, { signal })).data.data,
  handoff: async (id: number): Promise<{ url: string }> =>
    (await api.post(`/eligibility/checks/${id}/handoff`, {})).data.data,
  requestReview: async (
    id: number,
    message: string,
  ): Promise<EligibilityReview> =>
    (await api.post(`/eligibility/checks/${id}/review`, { message })).data.data,
  criteria: async (
    offeringId: number,
    signal?: AbortSignal,
  ): Promise<EligibilityPolicy[]> =>
    (
      await api.get(`/eligibility/admin/offerings/${offeringId}/criteria`, {
        signal,
      })
    ).data.data,
  createCriteria: async (
    offeringId: number,
    body: EligibilityPolicyBody,
  ): Promise<EligibilityPolicy> =>
    (
      await api.post(
        `/eligibility/admin/offerings/${offeringId}/criteria`,
        body,
      )
    ).data.data,
  approve: async (id: number): Promise<EligibilityPolicy> =>
    (await api.post(`/eligibility/admin/criteria/${id}/approve`, {})).data.data,
  reviews: async (
    page = 1,
    signal?: AbortSignal,
  ): Promise<{
    items: (EligibilityReview & { check: EligibilityCheck })[];
    total: number;
    page: number;
    canReassign: boolean;
    assignmentCandidates: { id: number; name: string }[];
  }> =>
    (await api.get("/eligibility/admin/reviews", { params: { page }, signal }))
      .data.data,
  claim: async (id: number): Promise<EligibilityReview> =>
    (await api.post(`/eligibility/admin/reviews/${id}/claim`, {})).data.data,
  assign: async (
    id: number,
    body: {
      ownerAdminId: number;
      reason: string;
      expectedOwnerAdminId: number | null;
    },
  ): Promise<EligibilityReview> =>
    (await api.post(`/eligibility/admin/reviews/${id}/assign`, body)).data.data,
  decide: async (
    id: number,
    body: { outcome: EligibilityOutcome; note: string; validUntil?: string },
  ): Promise<EligibilityReview> =>
    (await api.post(`/eligibility/admin/reviews/${id}/decision`, body)).data
      .data,
});
